import { getTokensFromLocalStorage } from '../storage-util';

/**
 * `USER_INFO_TOKEN` / `LocaleId.US`, inlined.
 *
 * Both live DOWNSTREAM of this library — the key in `@miq/platform`
 * (services/app-storage.service.ts) and the enum in `@miq/shared-models`
 * (models/language.model.ts) — and the library order is strictly one-way
 * (`@miq/utils` -> `@miq/shared-models` -> `@miq/platform`), so neither can be imported here.
 * They are both frozen values: `'userInfo'` is a localStorage key with ~10 years of persisted data
 * behind it, and `LocaleId.US = 1` is pinned by the EDP catalog.
 */
const USER_INFO_TOKEN = 'userInfo';
const LOCALE_ID_US = 1;

/**
 * L2-S6: `@miq/platform`'s `isUSLocale()` (services/user-preferences.ts), reproduced with the
 * dependency chain this library can actually reach.
 *
 * Platform's version is `const userInfo = getUserInfoWithSSO(); return !userInfo || userInfo.userLocaleId === LocaleId.US;`
 * — and `getUserInfoWithSSO()` is itself just `getTokensFromLocalStorage()[USER_INFO_TOKEN]` +
 * `JSON.parse`, wrapped in an SSO-retry side effect. `getTokensFromLocalStorage()` already lives in
 * this library (../storage-util) and is impersonation-aware, so the READ is identical, including
 * while impersonating.
 *
 * The SSO-RETRY SIDE EFFECT IS DELIBERATELY NOT COPIED. On a session token with missing/unparsable user info, platform
 * may re-enter SSO login (see /platform sso-user-info.ts) and returns `null` — and `!userInfo` makes
 * `isUSLocale()` return `true`. Returning `true` here is therefore OUTCOME-IDENTICAL for the format
 * decision, and firing a second, independent login redirect out of a date-format lookup would be a
 * bug, not a fidelity improvement.
 */
function isUSLocale(): boolean {
  const rawUserInfo = getTokensFromLocalStorage()[USER_INFO_TOKEN];
  if (!rawUserInfo) {
    return true;
  }
  try {
    const userInfo = JSON.parse(rawUserInfo);
    return !userInfo || userInfo.userLocaleId === LOCALE_ID_US;
  } catch {
    // Unparsable is treated exactly as missing — see the note above.
    return true;
  }
}

export class UserSettings {
  private static userViewOptions: any = {};

  /**
   * @param key `'code'` for an Angular `formatDate` pattern, anything else (conventionally
   * `'value'`) for a moment pattern.
   *
   * L2-S6: the NO-SAVED-PREFERENCE fallback now matches `@miq/platform`'s
   * `getGlobalDateDisplayFormat` (services/user-preferences.ts) instead of returning the US format
   * unconditionally. Both readers read the same `USER_SETTINGS` localStorage entry and return
   * `globalDateFormat[0][key]` when the user HAS a saved preference, so they only ever differed on
   * this branch — and that divergence meant a non-US user who had never saved a date preference got
   * US-ordered dates (`M/d/yyyy`) from this copy and correct ones (`dd/MM/yyyy`) from platform's.
   * That is the blocker recorded on `MiqMoment.getFormattedDate` in ../misc/sanitizers.ts; closing
   * it is what lets the reports remote's 30 date call sites be repointed off the host file.
   *
   * `extended` (platform's second parameter, which appends `' h:mm:ss a'`) is NOT reproduced: no
   * caller of this method passes it, and adding a parameter no one uses would just be a second thing
   * to keep in sync.
   */
  static getGlobalDateDisplayFormat(key: string = 'code'): string {
    const { globalDateFormat } = this.getUserViewOptions();
    const dateOpt = !!globalDateFormat && globalDateFormat.length ? globalDateFormat[0] : null;
    if (!!dateOpt) {
      return dateOpt[key];
    }
    // When no format preference is saved, derive the default from the user's locale.
    const usFormat = isUSLocale();
    return key !== 'code' ? (usFormat ? 'MM/DD/YYYY' : 'DD/MM/YYYY') : usFormat ? 'M/d/yyyy' : 'dd/MM/yyyy';
  }

  static getRentConfiguration() {
    const { rents } = this.getUserViewOptions();
    return rents;
  }

  /**
   * Safely return if the user rent preference is yearly
   *
   * @return true by default or if user rent settings is yearly
   */
  static isYearlyPreference() {
    const rents = UserSettings.getRentConfiguration();
    const rentsOpt = !!rents && rents.length > 0 ? rents[0] : null;
    // define display preference (by default is yearly)
    return !!rentsOpt ? !!rentsOpt && rentsOpt.code === 'Yearly' : true;
  }

  private static getUserViewOptions() {
    const userProfileSettings = localStorage.getItem('USER_SETTINGS')
      ? JSON.parse(localStorage.getItem('USER_SETTINGS'))
      : null;
    if (
      userProfileSettings != null &&
      userProfileSettings.userSettings != null &&
      userProfileSettings.userSettings.viewOptions != null
    ) {
      const { userSettings } = userProfileSettings;
      const { viewOptions } = userSettings;
      this.userViewOptions = viewOptions;
    } else {
      this.userViewOptions = { rents: null, globalDateFormat: null };
    }
    return this.userViewOptions;
  }
}
